import {useMemo} from 'react';

import {
    addManyToSelection,
    addToSelection,
    clearSelection,
    removeFromSelection,
} from '../reducers';
import {selectionSelector} from '../selectors';

import {
    useAppDispatch,
    useAppSelector,
} from '../../../hooks/useTypedReactRedux';

/**
 * FNP-918 / FNP-951: row selection for the cards table.
 *
 * Selection lives in the Redux `cards.selection` slice, per
 * MODULE_FEATURE_CHECKLIST §3. It was previously `useState<Card[]>` held in
 * IndexPage and threaded through RecordsTable as two props — which works until
 * a sibling outside that subtree (the header's delete button) needs to read it.
 *
 * The slice stores the whole record keyed by id. Consumers here read
 * `selection[id]` as a BOOLEAN, so the boolean map is derived below rather than
 * returned raw — handing back the record map would make an `=== true` check
 * false for every genuinely-selected row.
 *
 * ⚠️ The store is app-scoped, so selection SURVIVES unmount and route changes.
 * That is the point (a sibling outside this hook's owner can read it), but it
 * means anything that finishes a batch action must clear explicitly — IndexPage
 * passes handleClearSelection as onDeleteComplete for exactly that. It also
 * means tests need a fresh store per render; see tests/harness.tsx.
 */
export interface UseCardsSelectionResult {
    /** id -> true for every selected row. */
    selection: Record<string, boolean>;
    selectedIds: string[];
    handleRowSelection: (id: string, checked: boolean) => void;
    handleSelectAllRows: (checked: boolean, items: {id: string}[]) => void;
    handleClearSelection: () => void;
}

export const useCardsSelection = (): UseCardsSelectionResult => {
    const dispatch = useAppDispatch();

    const selected = useAppSelector(selectionSelector);

    const selectedIds = useMemo(() => Object.keys(selected), [selected]);

    const selection: Record<string, boolean> = useMemo(
        () => selectedIds.reduce<Record<string, boolean>>(
            (acc, id) => ({
                ...acc,
                [id]: true,
            }),
            {},
        ),
        [selectedIds],
    );

    const handleRowSelection = (id: string, checked: boolean) => {
        if (checked) {
            dispatch(addToSelection({id}));
        } else {
            dispatch(removeFromSelection(id));
        }
    };

    const handleSelectAllRows = (checked: boolean, items: {id: string}[]) => {
        if (checked) {
            // One dispatch for the whole page rather than one per row — see
            // addManyToSelection in SelectionSliceFor.
            dispatch(addManyToSelection(items.map(item => ({id: item.id}))));
        } else {
            dispatch(clearSelection());
        }
    };

    const handleClearSelection = () => {
        dispatch(clearSelection());
    };

    return {
        selection,
        selectedIds,
        handleRowSelection,
        handleSelectAllRows,
        handleClearSelection,
    };
};
